/**
 * Utilitários para textos temáticos do Welcome Wizard
 * Interpola placeholders e faz fallback para o tema default
 */

// O template default é importado estaticamente — usado como fallback.
// Todos os templates são empacotados em build time (import.meta.glob eager),
// então não há motivo para este módulo ser assíncrono.
import defaultTemplate from '../themes/templates/default.json';

/**
 * Substitui placeholders em um texto
 * Placeholders suportados: {firstName}, {professionalName}, {locationName}, {businessNoun}
 * 
 * @param {string} text - Texto com placeholders
 * @param {Object} vars - Dicionário de valores para substituição
 * @returns {string} Texto com placeholders substituídos
 */
const interpolateText = (text, vars = {}) => {
  if (!text) return '';
  
  let result = text;
  const placeholders = {
    '{firstName}': vars.firstName ?? '',
    '{professionalName}': vars.professionalName ?? '',
    '{locationName}': vars.locationName ?? '',
    '{businessNoun}': vars.businessNoun ?? '',
  };
  
  Object.entries(placeholders).forEach(([key, value]) => {
    result = result.replaceAll(key, value);
  });
  
  return result;
};

/**
 * Obtém um texto temático com fallback para o default
 * 
 * @param {Object} template - Template carregado do tema
 * @param {string} key - Chave da seção welcome, ex: 'userNameTitle'
 * @param {Object} vars - Variáveis para interpolação
 * @returns {string} Texto interpolado (síncrono — seguro para usar direto no JSX)
 *
 * Exemplos:
 *   getWelcomeText(tema, 'userNameTitle', {})
 *   getWelcomeText(tema, 'locationTitle', {firstName: 'João'})
 */
export const getWelcomeText = (template, key, vars = {}) => {
  // Tenta o template específico; cai no default; por fim string vazia.
  const text = template?.welcome?.[key] ?? defaultTemplate?.welcome?.[key];

  if (!text) {
    console.warn(`[WelcomeText] Chave não encontrada em nenhum template: ${key}`);
    return '';
  }

  return interpolateText(text, vars);
};

/**
 * Obtém um array temático (ex: serviceSuggestions, processingMessages)
 * com fallback para o default
 * 
 * @param {Object} template - Template carregado do tema
 * @param {string} key - Chave da seção welcome, ex: 'serviceSuggestions'
 * @returns {Array} Array do template, do default, ou array vazio (síncrono)
 *
 * Exemplos:
 *   getWelcomeList(tema, 'serviceSuggestions')
 *   getWelcomeList(tema, 'processingMessages')
 */
export const getWelcomeList = (template, key) => {
  let list = template?.welcome?.[key];

  if (!Array.isArray(list)) {
    list = defaultTemplate?.welcome?.[key];
  }

  if (!Array.isArray(list)) {
    console.warn(`[WelcomeText] Chave não encontrada ou não é array: ${key}`);
    return [];
  }

  return list;
};

/**
 * Interpola placeholders em um array de strings
 * Útil para arrays de mensagens de processamento
 * 
 * @param {Array<string>} items - Array de strings com placeholders
 * @param {Object} vars - Variáveis para interpolação
 * @returns {Array<string>} Array com placeholders interpolados
 */
export const interpolateList = (items, vars = {}) => {
  if (!Array.isArray(items)) return [];
  return items.map((item) => {
    if (typeof item === 'string') {
      return interpolateText(item, vars);
    }
    if (typeof item === 'object' && item.text) {
      return { ...item, text: interpolateText(item.text, vars) };
    }
    return item;
  });
};
